'use client'

import * as React from 'react'
import { Filter, Search } from 'lucide-react'
import { collection, getDocs, query, where } from 'firebase/firestore'
import { Badge } from 'components/ui/badge'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from 'components/ui/card'
import { Input } from 'components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'components/ui/table'
import { db } from '../../../../firebase'
import { useProject } from './project-context'

const AUDIT_COLLECTION = 'auditLogs'

function formatDateTime(value) {
  if (!value) return '—'
  if (typeof value?.toDate === 'function') {
    return value.toDate().toLocaleString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return '—'
  return parsed.toLocaleString('en-KE', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function ProjectAuditPage() {
  const { project } = useProject()
  const [searchQuery, setSearchQuery] = React.useState('')
  const [filterAction, setFilterAction] = React.useState('all')
  const [logs, setLogs] = React.useState([])
  const [isLoading, setIsLoading] = React.useState(true)

  React.useEffect(() => {
    let isMounted = true

    async function loadAuditLogs() {
      try {
        const projectQuery = query(
          collection(db, AUDIT_COLLECTION),
          where('entityId', '==', project.id),
        )
        const snapshot = await getDocs(projectQuery)
        let records = snapshot.docs.map((docItem) => ({
          id: docItem.id,
          ...docItem.data(),
        }))
        if (records.length === 0) {
          const allSnapshot = await getDocs(collection(db, AUDIT_COLLECTION))
          records = allSnapshot.docs.map((docItem) => ({
            id: docItem.id,
            ...docItem.data(),
          }))
        }
        if (!isMounted) return
        setLogs(records)
      } catch {
        if (!isMounted) return
        setLogs([])
      } finally {
        if (isMounted) setIsLoading(false)
      }
    }

    loadAuditLogs()

    return () => {
      isMounted = false
    }
  }, [project.id])

  const projectLogs = logs.filter(
    (log) =>
      log.entityId === project.id ||
      (String(log.entity || '').toLowerCase() === 'project' && log.entityId === project.id),
  )
  const allLogs = projectLogs.length > 0 ? projectLogs : logs

  const filteredLogs = allLogs.filter((log) => {
    const user = String(log.userDisplayName || log.user || '')
    const action = String(log.action || '')
    const entity = String(log.entity || '')
    const matchesSearch =
      searchQuery === '' ||
      user.toLowerCase().includes(searchQuery.toLowerCase()) ||
      action.toLowerCase().includes(searchQuery.toLowerCase()) ||
      entity.toLowerCase().includes(searchQuery.toLowerCase())
    const matchesAction =
      filterAction === 'all' || action.toLowerCase().includes(filterAction.toLowerCase())
    return matchesSearch && matchesAction
  })

  const uniqueActions = Array.from(new Set(allLogs.map((log) => log.action).filter(Boolean)))

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Audit Trail</CardTitle>
          <CardDescription>Complete log of all system actions and changes</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center mb-4">
            <div className="relative flex-1 max-w-sm">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
              <Input
                placeholder="Search by user, action, or entity..."
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={filterAction} onValueChange={setFilterAction}>
              <SelectTrigger className="w-48">
                <Filter className="size-3.5 mr-1.5" />
                <SelectValue placeholder="Filter by action" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                {uniqueActions.map((action) => (
                  <SelectItem key={action} value={action}>
                    {action}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="min-w-36">Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Entity</TableHead>
                  <TableHead className="min-w-48">Changes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                      Loading audit logs...
                    </TableCell>
                  </TableRow>
                ) : filteredLogs.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="py-6 text-center text-sm text-muted-foreground">
                      No audit logs matching your filters
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLogs.map((log) => (
                    <TableRow key={log.id}>
                      <TableCell className="text-xs text-muted-foreground whitespace-nowrap font-mono">
                        {formatDateTime(log.timestamp || log.createdAt)}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className="size-6 rounded-full bg-primary/10 flex items-center justify-center text-[9px] font-bold text-primary shrink-0">
                            {String(log.userDisplayName || log.user || 'U')
                              .split(' ')
                              .map((n) => n[0])
                              .join('')
                              .slice(0, 2)}
                          </div>
                          <span className="text-sm font-medium text-foreground whitespace-nowrap">
                            {log.userDisplayName || log.user || 'Unknown'}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {log.action || 'ACTION'}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm text-foreground">
                        {log.entity || 'Entity'}
                        {log.entityId && (
                          <span className="text-muted-foreground text-xs ml-1">({log.entityId})</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="text-xs space-y-0.5">
                          {log.changes?.before && (
                            <div className="flex gap-1">
                              <span className="text-red-600 line-through">{log.changes.before}</span>
                            </div>
                          )}
                          {log.changes?.after && (
                            <div className="flex gap-1">
                              <span className="text-emerald-600">{log.changes.after}</span>
                            </div>
                          )}
                          {!log.changes?.before && !log.changes?.after && (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
